import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

/**
 * MetaDataService
 * Centralized service for managing SEO meta tags, Open Graph, Twitter Cards, and canonical URLs
 */
@Injectable({
  providedIn: 'root',
})
export class MetaDataService {
  private readonly baseUrl = environment.siteUrl;

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  /**
   * Sets page metadata including title, description, and keywords
   * @param title - Page title (can be translation key)
   * @param description - Page description (can be translation key)
   * @param keywords - Page keywords (can be translation key)
   */
  setMetadata(title: string, description: string, keywords?: string): void {
    const translateMetadata: PageMetadata = this.translateMetadata({
      title,
      description,
      keywords,
    });
    this.setTitle(translateMetadata.title);
    this.setMetaTags(translateMetadata);
  }

  /**
   * Sets dynamic metadata with parameter interpolation
   * @param title - Title with {{param}} placeholders
   * @param description - Description with {{param}} placeholders
   * @param params - Key-value pairs for interpolation
   */
  setDynamicMetadata(
    title: string,
    description: string,
    params: { [key: string]: string },
    keywords?: string
  ): void {
    const translatedTitle = this.translateService.instant(title, params);
    const translatedDescription = this.translateService.instant(description, params);
    const translatedKeywords = keywords
      ? this.translateService.instant(keywords, params)
      : undefined;

    this.setTitle(translatedTitle);
    this.metaService.updateTag({ name: 'description', content: translatedDescription });
    if (translatedKeywords) {
      this.metaService.updateTag({ name: 'keywords', content: translatedKeywords });
    }
  }

  /**
   * Sets Open Graph meta tags for social sharing
   */
  setOpenGraph(
    description: string,
    imgUrl: string,
    url: string,
    type: 'website' | 'article' = 'website'
  ): void {
    const title = this.titleService.getTitle();
    const translateMetadata: PageMetadata = this.translateMetadata({ title, description });
    const fullImgUrl = imgUrl.startsWith('http') ? imgUrl : `${this.baseUrl}${imgUrl}`;

    this.metaService.updateTag({ property: 'og:title', content: translateMetadata.title });
    this.metaService.updateTag({ property: 'og:description', content: translateMetadata.description });
    this.metaService.updateTag({ property: 'og:image', content: fullImgUrl });
    this.metaService.updateTag({ property: 'og:url', content: url });
    this.metaService.updateTag({ property: 'og:type', content: type });
    this.metaService.updateTag({ property: 'og:site_name', content: 'Racebet365' });
    this.metaService.updateTag({ property: 'og:locale', content: this.getCurrentLocale() });
  }

  /**
   * Sets Twitter Card meta tags
   */
  setTwitterCard(
    card: 'summary' | 'summary_large_image' = 'summary',
    site?: string
  ): void {
    const title = this.titleService.getTitle();
    const description = this.metaService.getTag('name="description"')?.content || '';

    this.metaService.updateTag({ name: 'twitter:card', content: card });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
    if (site) {
      this.metaService.updateTag({ name: 'twitter:site', content: site });
    }
  }

  /**
   * Sets canonical URL using proper <link rel="canonical"> tag
   * Fixed: Previously used meta tag instead of link tag
   */
  setCanonicalUrl(url: string): void {
    const canonicalUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    this.updateLinkTag('canonical', canonicalUrl);
    this.generateHreflangTags(canonicalUrl);
  }

  /**
   * Generates hreflang tags for international SEO
   * SSR-compatible implementation
   */
  generateHreflangTags(url: string): void {
    const availableLanguages = ['en', 'fr'];
    const currentLanguage = this.getCurrentLanguage();

    // Add x-default hreflang
    this.createHrefLangLinkElement(url, 'x-default');

    // Add language-specific hreflang tags
    availableLanguages.forEach((lang) => {
      // Language is a query parameter (?lang=fr), the same form the sitemap
      // and the home page use. There is no /fr/ path prefix in the build:
      // server.ts 301s it away, so a /fr/ alternate would only send crawlers
      // through a redirect.
      const separator = url.includes('?') ? '&' : '?';
      const langUrl = lang === 'en' ? url : `${url}${separator}lang=${lang}`;
      this.createHrefLangLinkElement(langUrl, Lang[lang as keyof typeof Lang] || lang);
    });
  }

  /**
   * Sets robots meta tag dynamically
   */
  setRobots(index: boolean = true, follow: boolean = true): void {
    const content = `${index ? 'index' : 'noindex'}, ${follow ? 'follow' : 'nofollow'}`;
    this.metaService.updateTag({ name: 'robots', content });
  }

  /**
   * Complete SEO setup for a page
   */
  setPageSeo(config: PageSeoConfig): void {
    // Set basic metadata
    this.setMetadata(config.title, config.description, config.keywords);

    // Set canonical URL
    if (config.canonicalUrl) {
      this.setCanonicalUrl(config.canonicalUrl);
    }

    // Set Open Graph
    this.setOpenGraph(
      config.description,
      config.image || '/assets/media/images/racebet365.png',
      config.canonicalUrl || '',
      config.type
    );

    // Set Twitter Card
    this.setTwitterCard('summary_large_image');

    // Set robots
    if (config.noIndex || config.noFollow) {
      this.setRobots(!config.noIndex, !config.noFollow);
    }
  }

  /**
   * Sets default meta tags for initial page load
   * Should be called once during app initialization
   * Uses environment-based URLs
   */
  setDefaultMetaTags(): void {
    const defaultImage = `${this.baseUrl}/assets/media/images/racebet365.png`;

    // Open Graph defaults
    this.metaService.updateTag({ property: 'og:url', content: `${this.baseUrl}/` });
    this.metaService.updateTag({ property: 'og:image', content: defaultImage });
    this.metaService.updateTag({ property: 'og:site_name', content: 'Racebet365' });

    // Twitter Card defaults
    this.metaService.updateTag({ name: 'twitter:url', content: `${this.baseUrl}/` });
    this.metaService.updateTag({ name: 'twitter:image', content: defaultImage });

    // Canonical URL
    this.updateLinkTag('canonical', `${this.baseUrl}/`);

    // Hreflang tags
    this.createHrefLangLinkElement(`${this.baseUrl}/?lang=en`, 'en');
    this.createHrefLangLinkElement(`${this.baseUrl}/?lang=fr`, 'fr');
    this.createHrefLangLinkElement(`${this.baseUrl}/`, 'x-default');

    // Preconnect for API
    this.updateLinkTag('preconnect', environment.apiUrl);
  }

  /**
   * Gets the base URL for this environment
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  // Private helper methods

  private setTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  private setMetaTags(metaData: PageMetadata): void {
    this.metaService.updateTag({ name: 'description', content: metaData.description });
    if (metaData.keywords) {
      this.metaService.updateTag({ name: 'keywords', content: metaData.keywords });
    }
  }

  private translateMetadata(metaData: PageMetadata): PageMetadata {
    return {
      title: this.translateService.instant(metaData.title),
      description: this.translateService.instant(metaData.description),
      keywords: metaData.keywords
        ? this.translateService.instant(metaData.keywords)
        : undefined,
    };
  }

  private getCurrentLanguage(): string {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('language') || 'en';
    }
    return 'en';
  }

  private getCurrentLocale(): string {
    const lang = this.getCurrentLanguage();
    return lang === 'fr' ? 'fr_FR' : 'en_US';
  }

  /**
   * Creates or updates a link tag in the document head
   */
  private updateLinkTag(rel: string, href: string): void {
    let link = this.document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;

    if (!link) {
      link = this.document.createElement('link');
      link.rel = rel;
      this.document.head.appendChild(link);
    }

    link.href = href;
  }

  /**
   * Creates hreflang link element - SSR compatible
   */
  createHrefLangLinkElement(href: string, hreflang: string): void {
    this.deleteExistingHrefLangLink(hreflang);

    const linkTag = this.document.createElement('link');
    linkTag.rel = 'alternate';
    linkTag.href = href;
    linkTag.hreflang = hreflang;
    this.document.head.appendChild(linkTag);
  }

  /**
   * Removes existing hreflang link
   */
  deleteExistingHrefLangLink(hreflang: string): void {
    const existingLink = this.document.querySelector(
      `link[rel="alternate"][hreflang="${hreflang}"]`
    );
    if (existingLink) {
      existingLink.remove();
    }
  }
}

// Interfaces and Types

export interface PageMetadata {
  title: string;
  description: string;
  keywords?: string;
}

export interface PageSeoConfig {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  image?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
  noFollow?: boolean;
}

export enum Lang {
  fr = 'fr-FR',
  en = 'en-US',
}
